import { type ComponentProps, forwardRef, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type PopoverContentProps = ComponentProps<typeof PopoverContent>;
type TooltipContentProps = ComponentProps<typeof TooltipContent>;

interface TooltipPopoverProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  tooltipLabel: string;
  tooltipSide?: TooltipContentProps['side'];
  triggerContent: ReactNode;
  triggerClassName?: string;
  triggerName?: string;
  triggerAriaLabel?: string;
  triggerDisabled?: boolean;
  contentSide?: PopoverContentProps['side'];
  contentAlign?: PopoverContentProps['align'];
  contentSideOffset?: PopoverContentProps['sideOffset'];
  contentCollisionPadding?: PopoverContentProps['collisionPadding'];
  contentClassName?: string;
  onCloseAutoFocus?: PopoverContentProps['onCloseAutoFocus'];
}

const TooltipPopover = forwardRef<HTMLDivElement, TooltipPopoverProps>(
  (
    {
      isOpen,
      onOpenChange,
      children,
      tooltipLabel,
      tooltipSide,
      triggerContent,
      triggerClassName,
      triggerName,
      triggerAriaLabel,
      triggerDisabled,
      contentSide,
      contentAlign,
      contentSideOffset,
      contentCollisionPadding,
      contentClassName,
      onCloseAutoFocus,
    },
    ref
  ) => {
    return (
      <Popover open={isOpen} onOpenChange={onOpenChange}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                className={cn(
                  'flex items-center justify-center rounded-full shadow-sm transition-colors border-none',
                  isOpen
                    ? 'bg-primary-subtle text-primary hover:bg-primary-subtle hover:text-primary'
                    : 'bg-white text-black',
                  triggerClassName
                )}
                name={triggerName}
                aria-label={triggerAriaLabel ?? tooltipLabel}
                disabled={triggerDisabled}
              >
                {triggerContent}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>

          {!isOpen && (
            <TooltipContent side={tooltipSide}>
              <p>{tooltipLabel}</p>
            </TooltipContent>
          )}
        </Tooltip>

        <PopoverContent
          ref={ref}
          side={contentSide}
          align={contentAlign}
          sideOffset={contentSideOffset}
          collisionPadding={contentCollisionPadding}
          onCloseAutoFocus={onCloseAutoFocus}
          className={contentClassName}
        >
          {children}
        </PopoverContent>
      </Popover>
    );
  }
);

TooltipPopover.displayName = 'TooltipPopover';

export default TooltipPopover;
